/**
 * Data hooks for release notes.
 *
 * None of these re-fetch when the language changes: the payload carries EN and
 * DE, and the components pick at render time. The dependency arrays below are
 * deliberately free of anything i18n.
 */

import { useCallback, useEffect, useState } from "react";
import {
  releaseNotesApi,
  ReleaseNotesApiError,
} from "@services/releaseNotesApi";
import type { Module, PublicReleaseNote } from "@models/releaseNotes";

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

const initialState = <T,>(): AsyncState<T> => ({
  data: null,
  loading: true,
  error: null,
});

export function useReleaseNotes(module?: Module) {
  const [state, setState] = useState<AsyncState<PublicReleaseNote[]>>(
    initialState,
  );

  const load = useCallback(() => {
    let cancelled = false;
    setState((previous) => ({ ...previous, loading: true, error: null }));

    releaseNotesApi
      .list({ module })
      .then((page) => {
        if (!cancelled) {
          setState({ data: page.items, loading: false, error: null });
        }
      })
      .catch((error: Error) => {
        if (!cancelled) setState({ data: null, loading: false, error });
      });

    return () => {
      cancelled = true;
    };
  }, [module]);

  useEffect(load, [load]);

  return { ...state, reload: load };
}

/**
 * The newest releases across all modules, for the header popover.
 *
 * `enabled` is the same gate `useUnseenReleases` takes, and for the same reason:
 * the shell's token accessor is registered in a parent effect while this hook
 * runs in a child one, and React commits child effects before parent ones.
 * Ungated, the first `latest` call left before `setReleaseNotesAccessTokenFn`,
 * `authHeaders()` returned `{}` and the endpoint answered 401. It defaults to
 * enabled so callers that do not have a gate to offer keep working unchanged.
 *
 * `enabled` is a dependency of the effect on purpose: re-running the fetch when
 * the gate opens is what lets the popover recover from an earlier failure.
 */
export function useLatestReleases(limit = 5, enabled = true) {
  const [state, setState] = useState<AsyncState<PublicReleaseNote[]>>(
    initialState,
  );

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setState((previous) => ({ ...previous, loading: true, error: null }));

    releaseNotesApi
      .latest(limit)
      .then((items) => {
        if (!cancelled) setState({ data: items, loading: false, error: null });
      })
      .catch((error: Error) => {
        if (cancelled) return;
        // A 401 is not a terminal state here, the same way it is not one in
        // useUnseenReleases. It means "no usable token yet", so it must leave
        // the hook recoverable rather than latch an `error` that the popover
        // reads as "render nothing" for the rest of the session.
        const unauthorised =
          error instanceof ReleaseNotesApiError && error.status === 401;
        setState({
          data: null,
          loading: false,
          error: unauthorised ? null : error,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, limit]);

  return state;
}

/**
 * Drives the post-release modal.
 *
 * The server decides what is unseen and returns the acknowledgement value, so
 * this hook holds no "have I shown it" state of its own beyond the current
 * render. Dismissing posts the marker and clears the list, and because the
 * marker lives server-side the modal does not come back on another device.
 *
 * A failure is swallowed on purpose. This runs on every entry into the app, and
 * a release-note outage must not put an error in front of someone trying to use
 * Extraction.
 */
export function useUnseenReleases(enabled = true) {
  const [items, setItems] = useState<PublicReleaseNote[]>([]);
  const [latestReleasedAt, setLatestReleasedAt] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    releaseNotesApi
      .unseen()
      .then((response) => {
        if (cancelled) return;
        setItems(response.items);
        setLatestReleasedAt(response.latest_released_at);
      })
      .catch((error: unknown) => {
        if (error instanceof ReleaseNotesApiError && error.status === 401) {
          return;
        }
        // Intentionally quiet: see the docstring.
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const dismiss = useCallback(() => {
    const upTo = latestReleasedAt;
    setItems([]);
    if (!upTo) return;
    // Fire and forget. If the POST fails the modal simply reappears next load,
    // which is a better failure than blocking the dismiss on a network round trip.
    void releaseNotesApi.markSeen(upTo).catch(() => {});
  }, [latestReleasedAt]);

  return { items, latestReleasedAt, dismiss };
}
